import {
	Collapse,
	Descriptions,
	Flex,
	Image,
	Segmented,
	Splitter,
	Typography,
	theme,
} from "antd";
import CollapsePanel from "antd/es/collapse/CollapsePanel";
import { lazy, type ReactNode, Suspense, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkFrontmatter from "remark-frontmatter";
import remarkGfm from "remark-gfm";
export type DocumentViewMode = "preview" | "raw" | "split";

// Monaco is large and only needed once a document actually enters raw/split view —
// load it on demand instead of bundling it wherever DocumentEditor is imported.
const Editor = lazy(() =>
	import("@monaco-editor/react").then((m) => ({ default: m.Editor })),
);

function parseFrontmatter(markdown: string) {
	const FRONTMATTER_REGEX = /^---\n([\s\S]*?)\n---\n?/;

	const match = markdown.match(FRONTMATTER_REGEX);

	if (!match) {
		return {
			data: {},
			content: markdown,
		};
	}

	const yaml = match[1];
	const content = markdown.replace(FRONTMATTER_REGEX, "");

	const data: Record<string, any> = {};

	let currentArrayKey: string | null = null;

	yaml.split("\n").forEach((line) => {
		if (line.trim().startsWith("- ") && currentArrayKey) {
			data[currentArrayKey].push(line.replace("- ", "").trim());
			return;
		}

		const separatorIndex = line.indexOf(":");

		if (separatorIndex === -1) {
			return;
		}

		const key = line.slice(0, separatorIndex).trim();
		const value = line.slice(separatorIndex + 1).trim();

		if (!value) {
			currentArrayKey = key;
			data[key] = [];
			return;
		}

		currentArrayKey = null;
		data[key] = value;
	});

	return {
		data,
		content,
	};
}

// Plain, compact headings — not big display titles.
type MdNode = { position?: { start: { offset?: number } } };
const HEADING_SIZES: Record<number, number> = { 1: 18, 2: 16, 3: 14, 4: 13 };
function makeHeading(level: 1 | 2 | 3 | 4) {
	return ({ children, node }: { children?: ReactNode; node?: MdNode }) => (
		<Typography.Title
			level={level}
			style={{
				fontSize: HEADING_SIZES[level],
				fontWeight: 600,
				lineHeight: 1.4,
				// a heading that opens the doc sits flush with the container's padding
				marginTop: level === 1 || node?.position?.start.offset === 0 ? 0 : 20,
				marginBottom: 8,
			}}
		>
			{children}
		</Typography.Title>
	);
}

export function DocumentViewSelector({
	initialViewMode,
	onViewModeChange,
	enabledViewModes = ["preview", "raw", "split"],
}: {
	initialViewMode: DocumentViewMode;
	onViewModeChange: (mode: DocumentViewMode) => void;
	enabledViewModes?: DocumentViewMode[];
}) {
	const [viewMode, setViewMode] = useState<DocumentViewMode>(initialViewMode);
	return (
		<Segmented
			value={viewMode}
			name="view-mode"
			onChange={(v) => {
				setViewMode(v as DocumentViewMode);
				onViewModeChange(v as DocumentViewMode);
			}}
			options={[
				...enabledViewModes.map((mode) => ({
					label: mode.charAt(0).toUpperCase() + mode.slice(1),
					value: mode,
				})),
			]}
		/>
	);
}

export function DocumentEditor({
	content,
	readOnly = true,
	viewMode = "preview",
	onDocumentChange,
}: {
	content: string;
	readOnly?: boolean;
	viewMode?: DocumentViewMode;
	onDocumentChange?: (content: string) => void;
}) {
	const {
		token: {
			padding,
			colorBorderSecondary,
			colorFillAlter,
			colorTextSecondary,
		},
	} = theme.useToken();

	// frontmatter parsing
	const parsedDoc = useMemo(() => {
		return parseFrontmatter(content);
	}, [content]);

	const frontmatter = parsedDoc.data as Record<string, any>;
	const docText = parsedDoc.content;

	const editor = (
		<Suspense fallback={null}>
			<Editor
				height="100%"
				defaultLanguage="markdown"
				value={content}
				onChange={(v) => (onDocumentChange && v ? onDocumentChange(v) : null)}
				theme="vs-light"
				options={{
					minimap: { enabled: false },
					fontSize: 14,
					padding: { top: 16 },
					wordWrap: "on",
					scrollBeyondLastLine: false,
					readOnly,
				}}
			/>
		</Suspense>
	);

	const preview = (
		<div
			style={{
				maxWidth: 960,
				// in preview the host (card, drawer) already pads; split sits against the editor
				padding: viewMode === "split" ? padding : 0,
			}}
		>
			{frontmatter && Object.keys(frontmatter).length > 0 && (
				<Collapse style={{ marginBottom: 10 }}>
					<CollapsePanel key={"frontmatter"} header={"Metadata"}>
						<Descriptions
							column={1}
							size="small"
							items={Object.entries(frontmatter).map(([key, value]) => ({
								key,
								label: key,
								children: Array.isArray(value)
									? value.join(", ")
									: String(value ?? "-"),
							}))}
						/>
					</CollapsePanel>
				</Collapse>
			)}
			<ReactMarkdown
				remarkPlugins={[remarkGfm, remarkFrontmatter]}
				rehypePlugins={[rehypeHighlight]}
				components={{
					img: ({ src, alt }) => (
						<div style={{ margin: "24px 0" }}>
							<Image
								src={src || ""}
								alt={alt}
								style={{
									borderRadius: 16,
									overflow: "hidden",
									border: "1px solid #262626",
								}}
							/>
						</div>
					),
					h1: makeHeading(1),
					h2: makeHeading(2),
					h3: makeHeading(3),
					h4: makeHeading(4),
					p: ({ children }) => <Typography.Text>{children}</Typography.Text>,
					a: ({ href, children }) => (
						<Typography.Link href={href} target="_blank" rel="noreferrer">
							{children}
						</Typography.Link>
					),
					ul: ({ children }) => (
						<ul style={{ paddingLeft: 24, margin: "8px 0" }}>{children}</ul>
					),
					ol: ({ children }) => (
						<ol style={{ paddingLeft: 24, margin: "8px 0" }}>{children}</ol>
					),
					li: ({ children }) => <li style={{ margin: "4px 0" }}>{children}</li>,
					blockquote: ({ children }) => (
						<blockquote
							style={{
								borderLeft: `3px solid ${colorBorderSecondary}`,
								margin: "12px 0",
								padding: "4px 0 4px 16px",
								color: colorTextSecondary,
							}}
						>
							{children}
						</blockquote>
					),
					// GFM tables (remark-gfm); scroll horizontally on overflow
					table: ({ children }) => (
						<div style={{ overflowX: "auto", margin: "12px 0" }}>
							<table
								style={{
									borderCollapse: "collapse",
									width: "100%",
									fontSize: 14,
								}}
							>
								{children}
							</table>
						</div>
					),
					th: ({ children, style }) => (
						<th
							style={{
								border: `1px solid ${colorBorderSecondary}`,
								background: colorFillAlter,
								padding: "8px 12px",
								fontWeight: 600,
								textAlign: "left",
								...style, // GFM column alignment (:---:) comes through here
							}}
						>
							{children}
						</th>
					),
					td: ({ children, style }) => (
						<td
							style={{
								border: `1px solid ${colorBorderSecondary}`,
								padding: "8px 12px",
								...style,
							}}
						>
							{children}
						</td>
					),
					// pre wraps fenced code blocks; code handles inline vs block
					pre: ({ children }) => (
						<div
							style={{
								background: "#0a1633",
								color: "#e2e8f0",
								borderRadius: 12,
								border: "1px solid rgba(255,255,255,0.08)",
								overflowX: "auto",
								margin: "12px 0",
							}}
						>
							{children}
						</div>
					),
					code: ({ className, children }) => {
						// fenced blocks always have a trailing newline; className signals language
						const isBlock =
							Boolean(className) || String(children).endsWith("\n");
						if (isBlock) {
							return (
								<code
									className={className}
									style={{
										display: "block",
										fontFamily: "Menlo, Monaco, Consolas, monospace",
										fontSize: 13,
										lineHeight: 1.6,
										padding: 16,
										whiteSpace: "pre-wrap",
										background: "transparent",
										color: "inherit",
									}}
								>
									{children}
								</code>
							);
						}
						return (
							<code
								style={{
									fontFamily: "Menlo, Monaco, Consolas, monospace",
									fontSize: "0.875em",
									background: "rgba(0,0,0,0.06)",
									padding: "2px 6px",
									borderRadius: 4,
								}}
							>
								{children}
							</code>
						);
					},
				}}
			>
				{docText.trim()}
			</ReactMarkdown>
		</div>
	);

	return (
		<Flex orientation="vertical" style={{ height: "100%" }}>
			{/* Content */}
			{viewMode === "split" && (
				<Splitter>
					{/* Raw */}
					<Splitter.Panel collapsible min="20%" style={{ height: "100%" }}>
						{editor}
					</Splitter.Panel>
					{/* Preview */}
					<Splitter.Panel collapsible style={{ height: "100%" }}>
						{preview}
					</Splitter.Panel>
				</Splitter>
			)}

			{viewMode === "preview" && (
				<Flex orientation="vertical" style={{ height: "100%" }}>
					{preview}
				</Flex>
			)}
			{viewMode === "raw" && (
				<Flex orientation="vertical" style={{ height: "100%" }}>
					{editor}
				</Flex>
			)}
		</Flex>
	);
}
